<template>
  <intersect
    root-margin="150px 0px"
    @enter.once="isIntersected = true"
  >
    <div
      class="smart-countdown"
      :style="cssVariables"
    >
      <img
        :src="component.image"
        :alt="component.cta_text"
        class="img-fluid"
        loading="lazy"
      >
      <div class="countdown-type">
        {{ direction }} in
      </div>
      <client-only>
        <flip-countdown v-if="deadline" :deadline="deadline" />
        <div v-else class="countdown-type">
          a bit!
        </div>
      </client-only>
      <div class="countdown-buttons">
        <NuxtLink v-if="started" :to="more">
          View all deals
        </NuxtLink>
        <a
          v-if="component.cta_link"
          :href="component.cta_link"
          class="cta-link"
          target="_blank"
        >
          {{ component.cta_text }}
        </a>
      </div>
    </div>
  </intersect>
</template>
<script>
import Intersect from 'vue-intersect';
import FlipCountdown from 'vue2-flip-countdown';
import CampaignsMixin from '~/mixins/campaigns';

export default {
  components: {
    FlipCountdown,
    Intersect,
  },
  mixins: [CampaignsMixin],
  props: {
    more: {
      type: String,
      required: true,
    },
    component: {
      type: Object,
      required: true,
    },
    startDate: {
      type: String,
      required: false,
      default: null,
    },
    endDate: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isIntersected: false,
    };
  },
  computed: {
    deadline() {
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);

      if (!this.started && !this.ended) {
        return start.toLocaleString('en-us', {
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
        }).replace(/,/g, '');
      }

      if (this.started) {
        return end.toLocaleString('en-us', {
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
        }).replace(/,/g, '');
      }

      return null;
    },
    started() {
      const { now } = this;
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);

      if (this.startDate) {
        if (now >= start && now < end) {
          return true;
        }
      } else if (now < end) {
        return true;
      }

      return false;
    },
    ended() {
      const { now } = this;
      const end = new Date(this.endDate);

      return (now > end);
    },
    direction() {
      if (!this.started) {
        return 'Starts';
      }

      return 'Ends';
    },
    cssVariables() {
      if (!this.isIntersected) {
        return {};
      }

      return {
        '--background-image': `url(${this.component.background})`,
        '--mobile-background-image': `url(${this.component.mobile_background || this.component.background})`,
      };
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~/styles/utils/breakpoints';

.smart-countdown {
  margin: 20px -30px 0;
  padding: 64px;
  background-size: cover;
  background-position: bottom center;
  background-repeat: no-repeat;
  background-image: var(--background-image);
}

.smart-countdown > img {
  display: block;
  margin: 0 auto 24px;
}

.smart-countdown .countdown-type {
  font-weight: 600;
  font-size: 20px;
  line-height: 30px;
  margin-bottom: 8px;
  text-align: center;
  color: #09292b;
}

.smart-countdown .countdown-buttons {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.smart-countdown .countdown-buttons a {
  margin: 0 10px;
  background: #ffbc00;
  border-radius: 40px;
  color: #1b1b1b;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  padding: 16px 40px;
  text-decoration: none;
}

.smart-countdown .countdown-buttons .cta-link {
  background: #021414;
  color: #fff;
}

/* purgecss ignore */
.smart-countdown ::v-deep .flip-card {
  line-height: 1.15;
}

/* purgecss ignore */
.smart-countdown ::v-deep .flip-card__top,
.smart-countdown ::v-deep .flip-card__bottom,
.smart-countdown ::v-deep .flip-card__back-bottom,
.smart-countdown ::v-deep .flip-card__back::before,
.smart-countdown ::v-deep .flip-card__back::after {
  color: #fff;
}

/* purgecss ignore */
.smart-countdown ::v-deep .flip-card__top-4digits,
.smart-countdown ::v-deep .flip-card__bottom-4digits,
.smart-countdown ::v-deep .flip-card__back-bottom-4digits,
.smart-countdown ::v-deep .flip-card__back-4digits::before,
.smart-countdown ::v-deep .flip-card__back-4digits::after {
  color: #fff;
}

/* purgecss ignore */
.smart-countdown ::v-deep .flip-clock__slot {
  margin-top: 6px;
  text-transform: uppercase;
  color: #021414;
  font-weight: 600;
  font-size: 12px;
  line-height: 130%;
}

@include media-breakpoint-down(sm) {
  .smart-countdown {
    margin-left: -20px;
    margin-right: -20px;
    padding-left: 8px;
    padding-right: 8px;
    background-image: var(--mobile-background-image);
  }

  .smart-countdown > img {
    padding-left: 56px;
    padding-right: 56px;
  }

  .smart-countdown .countdown-buttons {
    flex-direction: column;
    max-width: 240px;
    margin: 0 auto;
  }

  .smart-countdown .countdown-buttons a {
    margin: 10px 0;
    text-align: center;
    width: 100%;
  }
}
</style>
